import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangleIcon, InfoIcon, RefreshCwIcon } from "lucide-react";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatNumber } from "@/lib/format";
import { MatchCountByToggle } from "./MatchCountByToggle";
import {
  bucketLabel,
  MATCH_SURVIVORSHIP_NOTE,
  MATCH_VIZ_CLASS,
  MATCH_VIZ_STYLES,
  toChartNumber,
} from "./matchOutcomeBuckets";
import { useAnalyticsParams, type MatchUnit } from "./useAnalyticsParams";
import { CHART_TOOLTIP_STYLE, chartTooltipProps } from "@/components/charts/ChartTooltip";

type MatchEventsResponse = components["schemas"]["MatchEventsResponse"];
type MatchEventRow = components["schemas"]["MatchEventRow"];
type AnalyticsOrganisationRow = components["schemas"]["AnalyticsOrganisationRow"];

const ENDPOINT = "/admin/analytics/match-events";
const HOURS_IN_DAY = 24;

/** Below this many matches no daily shape is real, so the section says so in words. */
const MIN_EVENTS_FOR_PATTERN = 10;

/** A run of hours has to hold this much of the day's matches before it is worth calling a busy period. */
const CONCENTRATION_SHARE = 0.6;

/** Past this many hours wide, the "busy period" covers a third of the day and stops being a claim. */
const MAX_CONCENTRATION_HOURS = 8;

/** Rows drawn in the details table at once. Everything past this is in the CSV, which is right there. */
const MAX_TABLE_ROWS = 200;

function buildQuery(apiParams: URLSearchParams, unit: MatchUnit) {
  return {
    From: apiParams.get("From") ?? "",
    To: apiParams.get("To") ?? "",
    ClientIds: apiParams.getAll("ClientIds"),
    OrganisationId: apiParams.get("OrganisationId") || undefined,
    Unit: unit,
  };
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** Hour of the UTC day as "09:00". */
export function hourLabel(hour: number): string {
  return `${pad2(((hour % HOURS_IN_DAY) + HOURS_IN_DAY) % HOURS_IN_DAY)}:00`;
}

/** "2026-09-08T09:41:07Z" -> 9. UTC throughout, so the day never shifts under the reader's timezone. */
function hourOfDayUtc(iso: string): number {
  return new Date(iso).getUTCHours();
}

/** "2026-09-08T09:41:07Z" -> "8 Sep 2026", parsed as UTC. */
function dateLabelUtc(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "2026-09-08T09:41:07Z" -> "09:41", UTC. */
function timeLabelUtc(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;
}

interface EventPoint {
  createdAt: string;
  hour: number;
  date: string;
  time: string;
  organisationId: string;
  organisationName: string;
  outcome: string;
  billId: string;
}

function toPoint(row: MatchEventRow): EventPoint {
  return {
    createdAt: row.createdAt,
    hour: hourOfDayUtc(row.createdAt),
    date: dateLabelUtc(row.createdAt),
    time: timeLabelUtc(row.createdAt),
    organisationId: row.organisationId,
    organisationName: row.organisationName,
    outcome: bucketLabel(row.bucket),
    billId: row.billId,
  };
}

export interface HourColumn {
  hour: number;
  label: string;
  count: number;
}

export function buildHourColumns(points: EventPoint[]): HourColumn[] {
  const columns: HourColumn[] = Array.from({ length: HOURS_IN_DAY }, (_, hour) => ({
    hour,
    label: hourLabel(hour),
    count: 0,
  }));
  for (const point of points) {
    const column = columns[point.hour];
    if (column) column.count += 1;
  }
  return columns;
}

/** The hour with the most matches. Ties go to the earlier hour, so the sentence is stable run to run. */
function busiestHour(columns: HourColumn[]): number {
  let best = 0;
  for (let hour = 1; hour < HOURS_IN_DAY; hour++) {
    if ((columns[hour]?.count ?? 0) > (columns[best]?.count ?? 0)) best = hour;
  }
  return best;
}

/** Narrowest run of consecutive hours holding CONCENTRATION_SHARE of matches, or null. Wraps midnight for night shifts. */
function busiestRun(columns: HourColumn[], total: number): { start: number; length: number } | null {
  const target = total * CONCENTRATION_SHARE;
  for (let length = 1; length <= MAX_CONCENTRATION_HOURS; length++) {
    let bestStart = -1;
    let bestCount = -1;
    for (let start = 0; start < HOURS_IN_DAY; start++) {
      let count = 0;
      for (let offset = 0; offset < length; offset++) {
        count += columns[(start + offset) % HOURS_IN_DAY]?.count ?? 0;
      }
      if (count > bestCount) {
        bestCount = count;
        bestStart = start;
      }
    }
    if (bestCount >= target) return { start: bestStart, length };
  }
  return null;
}

/** The headline sentence: too few matches, a busy period, or spread out; the busiest hour is always named. */
export function timeOfDayHeadline(columns: HourColumn[], total: number): string {
  if (total === 0) return "No matches in this window.";
  if (total < MIN_EVENTS_FOR_PATTERN) {
    const noun = total === 1 ? "match" : "matches";
    return `Only ${formatNumber(total)} ${noun} in this window, which is too few to show a pattern through the day. Every one of them is listed under Data details.`;
  }
  const peak = busiestHour(columns);
  const peakSentence = `Busiest hour: ${hourLabel(peak)} to ${hourLabel(peak + 1)} UTC.`;
  const run = busiestRun(columns, total);
  if (!run) {
    return `Matches are spread through the day, with no single busy period. ${peakSentence}`;
  }
  if (run.length === 1) return `Most matches happened in a single hour. ${peakSentence}`;
  return `Most matches happened between ${hourLabel(run.start)} and ${hourLabel(run.start + run.length)} UTC. ${peakSentence}`;
}

export interface MatchTimeOfDayChartProps {
  apiParams: URLSearchParams;
  unit: MatchUnit;
}

/** Section 1: when in the UTC day matches happen, one column per hour. Every raw timestamp is listed in Data details. */
export function MatchTimeOfDayChart({ apiParams, unit }: MatchTimeOfDayChartProps) {
  const apiParamsKey = apiParams.toString();
  const { organisationId, clientIds, from, to, setParam } = useAnalyticsParams();

  const [data, setData] = useState<MatchEventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data: response, error: apiError } = await api.GET(ENDPOINT, {
        params: { query: buildQuery(apiParams, unit) },
      });
      if (cancelled) return;
      if (apiError || !response) {
        setError("Match times could not be loaded.");
      } else {
        setData(response);
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
    // apiParamsKey stands in for apiParams (same input, same key); refreshKey only exists to retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiParamsKey, unit, refreshKey]);

  // Fetched, not derived from rows: once one org is picked the rows contain only it. Same endpoint as the filter bar.
  const clientIdsKey = clientIds.join(",");
  const [organisations, setOrganisations] = useState<AnalyticsOrganisationRow[]>([]);
  useEffect(() => {
    if (clientIds.length === 0) {
      setOrganisations([]);
      return;
    }
    let cancelled = false;
    async function loadOrganisations() {
      const { data: response } = await api.GET("/admin/analytics/organisations", {
        params: { query: { From: from, To: to, ClientIds: clientIds } },
      });
      if (!cancelled && response) setOrganisations(response);
    }
    void loadOrganisations();
    return () => {
      cancelled = true;
    };
    // clientIdsKey stands in for clientIds (same input, stable key).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdsKey, from, to]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const points = useMemo(() => rows.map(toPoint), [rows]);
  const columns = useMemo(() => buildHourColumns(points), [points]);
  const headline = useMemo(() => timeOfDayHeadline(columns, points.length), [columns, points.length]);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const filteredPoints = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    if (!needle) return points;
    return points.filter((point) =>
      `${point.date} ${point.time} ${point.organisationName} ${point.outcome} ${point.billId}`
        .toLowerCase()
        .includes(needle),
    );
  }, [points, debouncedSearch]);

  const totalCount = data ? toChartNumber(data.totalCount) : 0;
  const truncated = data?.truncated ?? false;
  const inconsistentRows = data ? toChartNumber(data.inconsistentRows) : 0;

  if (loading && !data) {
    return (
      <div className="flex flex-col gap-3">
        <div className="bg-muted h-6 w-2/3 animate-pulse rounded" />
        <div className="bg-muted h-64 animate-pulse rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-destructive/30 bg-destructive/5 flex flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center">
        <AlertTriangleIcon className="text-destructive size-6" />
        <p className="text-sm font-medium">Could not load match times</p>
        <p className="text-muted-foreground max-w-md text-sm">{error}</p>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="border-border hover:bg-accent mt-1 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition"
        >
          <RefreshCwIcon className="size-3.5" />
          Try again
        </button>
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div className="border-border flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
        <InfoIcon className="text-muted-foreground size-6" />
        <p className="text-sm font-medium">No matches in this window</p>
        <p className="text-muted-foreground max-w-md text-sm">
          Widen the date range, or check that the selected client has matching activity.
        </p>
      </div>
    );
  }

  const selectedOrganisation = organisationId
    ? organisations.find((org) => org.organisationId === organisationId)
    : undefined;
  const showOrganisationPicker = organisations.length > 1;

  return (
    <div className={`${MATCH_VIZ_CLASS} flex flex-col gap-5`}>
      <style>{MATCH_VIZ_STYLES}</style>

      <p className="text-lg leading-snug font-medium">{headline}</p>

      {showOrganisationPicker && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Organisation</span>
          <Select
            value={organisationId ?? "__all__"}
            onValueChange={(value) => setParam("org", value === "__all__" ? undefined : value)}
          >
            <SelectTrigger size="sm" className="max-w-64">
              <SelectValue placeholder="All organisations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All organisations</SelectItem>
              {organisations.map((org) => (
                <SelectItem key={org.organisationId} value={org.organisationId}>
                  {org.organisationName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!selectedOrganisation && (
            <span className="text-muted-foreground text-xs">
              Every selected organisation is counted together in the chart below.
            </span>
          )}
        </div>
      )}

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={columns} margin={{ top: 4, right: 4, bottom: 0, left: -18 }} barCategoryGap="14%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border, #e5e7eb)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
              stroke="var(--color-muted-foreground, #6b7280)"
              interval={1}
            />
            <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground, #6b7280)" allowDecimals={false} />
            <Tooltip
              cursor={chartTooltipProps.cursor}
              content={({ active, payload }) => {
                if (!active || !payload || payload.length === 0) return null;
                const column = payload[0]?.payload as HourColumn | undefined;
                if (!column) return null;
                return (
                  <div style={CHART_TOOLTIP_STYLE} className="px-2 py-1.5 text-xs">
                    <p className="font-medium">
                      {hourLabel(column.hour)} to {hourLabel(column.hour + 1)} UTC
                    </p>
                    <p className="text-muted-foreground">
                      {formatNumber(column.count)} {column.count === 1 ? "match" : "matches"}
                    </p>
                  </div>
                );
              }}
            />
            <Bar dataKey="count" name="Matches" fill="var(--series-1)" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-muted-foreground text-xs">
        Hours are UTC. Each column is the number of matches created in that hour, across every day in the
        selected range.
      </p>

      <Accordion type="single" collapsible className="border-t">
        <AccordionItem value="details" className="border-b-0">
          <AccordionTrigger>Data details</AccordionTrigger>
          <AccordionContent className="space-y-5 text-sm">
            <MatchCountByToggle unit={unit} onChange={(next) => setParam("unit", next)} />

            <div>
              <div className="flex items-center gap-1.5">
                <p className="font-medium">Every match, with its exact time</p>
                <InfoHint label="Every match, with its exact time">
                  The raw created time of every match in the selected range, newest handling of the same
                  day kept in the order the system returned them. Times are UTC.
                </InfoHint>
              </div>
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by date, organisation, outcome or bill id"
                className="mt-2 h-8 max-w-96"
                aria-label="Search matches"
              />
              <p className="text-muted-foreground mt-2 text-xs">
                {formatNumber(filteredPoints.length)} of {formatNumber(points.length)}{" "}
                {points.length === 1 ? "match" : "matches"} shown
                {filteredPoints.length > MAX_TABLE_ROWS
                  ? `. The table lists the first ${formatNumber(MAX_TABLE_ROWS)}; the CSV has all of them.`
                  : "."}
              </p>
              <div className="mt-2 max-h-96 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Time (UTC)</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Outcome</TableHead>
                      <TableHead>Bill</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPoints.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-muted-foreground py-6 text-center">
                          Nothing matches that search.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPoints.slice(0, MAX_TABLE_ROWS).map((point, index) => (
                        <TableRow key={`${point.billId}-${point.createdAt}-${index}`}>
                          <TableCell className="whitespace-nowrap">{point.date}</TableCell>
                          <TableCell className="tabular-nums whitespace-nowrap">{point.time}</TableCell>
                          <TableCell className="max-w-48 truncate">{point.organisationName}</TableCell>
                          <TableCell className="whitespace-nowrap">{point.outcome}</TableCell>
                          <TableCell className="font-mono text-xs">{point.billId.slice(0, 8)}...</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {truncated && (
              <p className="text-muted-foreground text-xs">
                The page loaded the first {formatNumber(rows.length)} of {formatNumber(totalCount)} matches.
                Download the CSV for all of them.
              </p>
            )}

            {inconsistentRows > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0" />
                <span>
                  Possible counting issue: {formatNumber(inconsistentRows)} matched{" "}
                  {inconsistentRows === 1 ? "line is" : "lines are"} recorded as both matched manually and
                  matched automatically. That should not happen, so the automatic figures may be too high.
                </span>
              </div>
            )}

            <p className="text-muted-foreground text-xs">{MATCH_SURVIVORSHIP_NOTE}</p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
